// services/backendService.ts
import type { CategoryUtilization, CompanyMetrics } from '@/types/metrics'
import type {
  ActivationReportResponse,
  MemberActivityResponse,
  MemberTimelineResponse,
} from '@/types/member-activity'
import type { Organization } from '@/types/organization'
import type {
  CoachEarning,
  CoachEarningsReport,
  CoachLinkAdmin,
  CoachLinkSuspendRequest,
  CoachPayoutRequest,
  CoachPayoutResult,
} from '@/types/coach-earnings'
import type {
  CatalogMissesResponse,
  CatalogProductsPage,
  CatalogProductsQuery,
  CatalogRecordKind,
  CatalogRecordResponse,
} from '@/types/supplement-catalog'
import { BackendError } from '@/services/backendError'
import { useUserStore } from '@/stores/user'

// GET /admin/me: the caller's super-admin flag and the organizations they
// own or administer (services/ai AdminMeResponse).
export interface AdminMeResponse {
  is_super_admin: boolean
  organizations: Organization[]
}

export interface CreateOrganizationResponse {
  organization_id: string
  name: string
  owner_email: string
  owner_status: 'added' | 'invited'
}

export interface AdhocEmailRecipientInput {
  email: string
  name?: string
}

export interface SendAdhocEmailPayload {
  subject: string
  html: string
  recipients: AdhocEmailRecipientInput[]
  reply_to?: string
  is_test: boolean
}

export interface AdhocEmailSendResult {
  email: string
  success: boolean
  error: string | null
}

export interface SendAdhocEmailResponse {
  status: 'sent' | 'scheduled'
  recipient_count: number
  invalid_count: number
  unsubscribed: string[]
  unsubscribed_count: number
  sent_count: number | null
  failed_count: number | null
  results: AdhocEmailSendResult[] | null
}

export interface FormatAdhocEmailPayload {
  body: string
  subject?: string
}

export interface FormatAdhocEmailResponse {
  body_html: string
  // body_html wrapped in the branded shell server-side, ready to preview/send.
  html: string
  subject: string | null
  summary: string
}

// A campaign starter template. The registry (single home of campaign copy)
// lives in services/ai/app/domains/adhoc_email/templates.py.
export interface AdhocEmailTemplateItem {
  id: string
  label: string
  description: string
  subject: string
  reply_to: string
  html: string
}

export interface ListAdhocEmailTemplatesResponse {
  templates: AdhocEmailTemplateItem[]
  placeholder_token: string
}

export interface DietEmailResponse {
  email: string
  canRegenerate: boolean
}

export interface DietSettingsResponse {
  email: string
  menuAdditionalInfo: string | null
  canEdit: boolean
}

export interface UpdateMenuInfoResponse {
  menuAdditionalInfo: string
  message: string
}

export interface RegenerateDietEmailResponse {
  email: string
  message: string
}

export interface OrganizationPlan {
  organization_plan_id: string
  organization_id: string
  current: boolean
  plan_name: string | null
  product_discount: number
  monthly_credit: number
  org_partner_quota: number
  org_partner_discount: number
  org_partner_credit: number
  start_date: string | null
  end_date: string | null
  created_at: string
  updated_at: string
}

export interface OrganizationPlanResponse {
  organization_id: string
  plan: OrganizationPlan | null
  can_edit: boolean
}

export interface UpsertOrganizationPlanPayload {
  current: boolean
  plan_name: string | null
  product_discount: number
  monthly_credit: number
  org_partner_quota: number
  org_partner_discount: number
  org_partner_credit: number
  start_date: string | null
  end_date: string | null
}

export interface UserPlan {
  plan_id: string
  user_id: string
  organization_plan_id: string | null
  linked_member_plan_id: string | null
  type: string
  current: boolean
  plan_name: string | null
  product_discount: number
  monthly_credit: number
  org_partner_quota: number
  org_partner_discount: number
  org_partner_credit: number
  start_date: string | null
  end_date: string | null
  created_at: string
  updated_at: string
}

export interface UserPlansResponse {
  user_id: string
  plans: UserPlan[]
}

export interface SubscriptionLookupOrganization {
  organization_id: string
  role: string | null
  organization_name: string | null
}

export interface SubscriptionLookupResponse {
  user_id: string | null
  email: string
  organizations: SubscriptionLookupOrganization[]
  plans: UserPlan[]
}

export interface AdHocIndividualPlanPayload {
  plan_name?: string | null
  product_discount?: number
  monthly_credit?: number
  start_date?: string | null
  end_date?: string | null
}

export interface BonusDaysResponse {
  status: 'applied' | 'skipped' | 'failed'
  applied_via: 'appstle_billing_date' | 'plan_end_date' | 'access_row' | null
  detail: string | null
  new_date: string | null
}

export interface OrganizationInvitation {
  invitation_id: string
  email: string
  invitation_type: 'org_member' | 'org_partner' | 'b2c_referral' | 'generic'
  organization_id: string | null
  role: 'member' | 'admin' | 'owner' | null
  accepted: boolean
  accepted_at: string | null
  created_at: string
  invited_by: string | null
}

export interface OrganizationInviteResult {
  email: string
  link?: string | null
  error?: string | null
}

export interface InviteUsersApiResponse {
  results: OrganizationInviteResult[]
}

export interface Benefit {
  benefit_id: string
  category: 'fitness_access' | 'mental_health' | 'healthy_food' | 'movement_ergonomics' | 'recovery_time' | 'preventive_screening'
  label: string
  short_description: string | null
  long_description: string | null
  source: 'parsed' | 'hr_edited' | 'manual'
}

export interface ShareLinkResponse {
  url: string
  slug: string
}

export type OpenJoinOnboarding = 'regular' | 'endurance' | 'imessage'

export interface OpenJoinLinkResponse {
  url: string
  slug: string
  join_token: string
}

export type NoteMode = 'standing' | 'notice'
export type NoteAudience = 'user' | 'organization' | 'everyone'
export type NoteStatus = 'draft' | 'live' | 'retired'

export interface AuthoredNote {
  note_id: string
  mode: NoteMode
  author_kind: 'mos' | 'org'
  author_name: string
  organization_id: string | null
  audience: NoteAudience
  audience_user_ids: string[] | null
  title: string | null
  body: string
  question: string | null
  severity: 'normal' | 'critical'
  starts_at: string | null
  expires_at: string | null
  status: NoteStatus
  created_at: string
  updated_at: string
  retired_at: string | null
}

export interface CreateNotePayload {
  mode: NoteMode
  audience: NoteAudience
  body: string
  title?: string | null
  question?: string | null
  organization_id?: string | null
  audience_user_ids?: string[] | null
  author_name?: string | null
  severity?: 'normal' | 'critical'
  starts_at?: string | null
  expires_at?: string | null
}

export interface PublishNoteResponse {
  note: AuthoredNote
  delivered_to: number
}

// Verified primary-contact address per user id named by a `user` audience, so
// the list can say who a note reached instead of "3 specific people". A
// recipient with no verified address is simply absent from the map.
export interface NoteListResponse {
  notes: AuthoredNote[]
  recipient_emails: Record<string, string>
}

// One token the author typed that named a real Mos account. `email` is for
// showing them who they are about to reach and may be null.
export interface ResolvedAudienceMember {
  token: string
  user_id: string
  email: string | null
}

export interface ResolveAudienceResponse {
  resolved: ResolvedAudienceMember[]
  unknown: string[]
}

export interface OrganizationIdentityPatch {
  name?: string
  slug?: string
  domains?: string[]
}

export interface UpdateOrganizationResponse {
  organization_id: string
  name: string
  slug: string | null
  domains: string[] | null
}

async function readBackendError(response: Response): Promise<BackendError> {
  const fallback = `HTTP error! status: ${response.status}`
  let message = fallback
  let code: string | null = null
  try {
    const body: unknown = await response.json()
    if (body && typeof body === 'object') {
      const o = body as Record<string, unknown>
      if (o.detail && typeof o.detail === 'object' && !Array.isArray(o.detail)) {
        const detailCode = (o.detail as Record<string, unknown>).code
        if (typeof detailCode === 'string') code = detailCode
      }
      if (typeof o.detail === 'string' && o.detail.trim()) message = o.detail
      else if (typeof o.error === 'string' && o.error.trim()) message = o.error
      else if (typeof o.message === 'string' && o.message.trim()) message = o.message
    }
  } catch {
    /* ignore malformed JSON */
  }
  return new BackendError(message, response.status, code)
}

export const useBackendService = () => {
  const userStore = useUserStore()
  const deployEnv = import.meta.env.VITE_ENV || import.meta.env.VITE_VERCEL_ENV || 'development'
  const apiUrl =
    import.meta.env.VITE_API_URL ||
    (deployEnv === 'production' ? 'https://ai.moshealth.com' : 'https://staging-ai.moshealth.com')

  // Single entry point for every backend call: attaches the bearer token,
  // forwards the caller's method/body/headers, normalizes error extraction via
  // readBackendError (a BackendError), and parses the JSON response. Callers that send JSON
  // pass their own Content-Type; FormData callers omit it so the browser sets
  // the multipart boundary.
  const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    const accessToken = await userStore.getAccessToken()
    const response = await fetch(`${apiUrl}${path}`, {
      ...init,
      headers: {
        ...init.headers,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      throw await readBackendError(response)
    }

    return response.json() as Promise<T>
  }

  const getAdminMe = (): Promise<AdminMeResponse> => request('/admin/me')

  const sendInvitations = (
    emails: string[],
    organizationId: string,
  ): Promise<InviteUsersApiResponse> =>
    request('/admin/invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails, organization_id: organizationId }),
    })

  const regenerateDietEmail = (organizationId: string): Promise<RegenerateDietEmailResponse> =>
    request(`/diet/organization/${organizationId}/settings/email/regenerate`, {
      method: 'POST',
    })

  const fetchDietSettings = (organizationId: string): Promise<DietSettingsResponse> =>
    request(`/diet/organization/${organizationId}/settings`)

  const updateMenuAdditionalInfo = (organizationId: string, menuAdditionalInfo: string): Promise<UpdateMenuInfoResponse> =>
    request(`/diet/organization/${organizationId}/settings/menu-info`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ menuAdditionalInfo }),
    })

  const createOrganization = (
    name: string,
    domain: string,
    ownerEmail: string
  ): Promise<CreateOrganizationResponse> =>
    request('/admin/organizations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, domain, owner_email: ownerEmail }),
    })

  const getOrganizationPlan = (organizationId: string): Promise<OrganizationPlanResponse> =>
    request(`/admin/subscriptions/organizations/${organizationId}/plan`)

  const getShareLink = (organizationId: string): Promise<ShareLinkResponse> => {
    const params = new URLSearchParams({ organization_id: organizationId })
    return request(`/admin/org/share-link?${params.toString()}`)
  }

  const getOpenJoinLink = (
    organizationId: string,
    onboarding: OpenJoinOnboarding,
  ): Promise<OpenJoinLinkResponse> => {
    const params = new URLSearchParams({ organization_id: organizationId, onboarding })
    return request(`/admin/org/open-join-link?${params.toString()}`)
  }

  const createOrganizationPlan = (
    organizationId: string,
    payload: UpsertOrganizationPlanPayload
  ): Promise<{ plan: OrganizationPlan }> =>
    request(`/admin/subscriptions/organizations/${organizationId}/plan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

  const updateOrganizationPlan = (
    organizationId: string,
    organizationPlanId: string,
    payload: Partial<UpsertOrganizationPlanPayload>
  ): Promise<{ plan: OrganizationPlan }> =>
    request(`/admin/subscriptions/organizations/${organizationId}/plan/${organizationPlanId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

  const getSubscriptionLookupByEmail = (
    email: string,
  ): Promise<SubscriptionLookupResponse> => {
    const params = new URLSearchParams({ email: email.trim() })
    return request(`/admin/users/by-email?${params.toString()}`)
  }

  const createAdHocIndividualPlan = (
    userId: string,
    payload: AdHocIndividualPlanPayload,
  ): Promise<{ plan: UserPlan }> =>
    request(`/admin/users/${encodeURIComponent(userId)}/plans/ad-hoc-individual`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

  const grantBonusDays = (userId: string, days: number): Promise<BonusDaysResponse> =>
    request(`/admin/users/${encodeURIComponent(userId)}/bonus-days`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ days }),
    })

  const setCurrentUserPlan = (
    userId: string,
    planId: string,
  ): Promise<{ plan: UserPlan }> =>
    request(`/admin/users/${encodeURIComponent(userId)}/plans/${encodeURIComponent(planId)}/set-current`, {
      method: 'POST',
    })

  const getUserPlans = (userId: string): Promise<UserPlansResponse> =>
    request(`/admin/users/${encodeURIComponent(userId)}/plans`)

  const getOrganizationInvitations = (
    organizationId: string
  ): Promise<{ invitations: OrganizationInvitation[] }> => {
    const params = new URLSearchParams({ organization_id: organizationId })
    return request(`/admin/invitations?${params.toString()}`)
  }

  const resendOrganizationInvitation = (invitationId: string): Promise<{ success: boolean }> =>
    request('/admin/invitations/resend', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invitation_id: invitationId }),
    })

  const deactivateUserPlan = (planId: string): Promise<{ plan: UserPlan }> =>
    request(`/admin/plans/${encodeURIComponent(planId)}/deactivate`, {
      method: 'PATCH',
    })

  const cancelOrganizationInvitation = (invitationId: string): Promise<{ success: boolean }> =>
    request('/admin/invitations/cancel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invitation_id: invitationId }),
    })

  // Authored notes. A standing note is background the member's Mos reads; a
  // notice is delivered once through the outreach planner. Both are drafts
  // until publishNote. (Not the org welcome message — that is static
  // pre-signup marketing copy and stays a column on the organization.)
  const listNotes = (organizationId?: string): Promise<NoteListResponse> =>
    request(
      `/admin/notes${organizationId ? `?organization_id=${organizationId}` : ''}`,
    )

  // Composing only — nothing is stored. Turns pasted emails and user ids into
  // the ids a `user` audience needs, and names the addresses no Mos account
  // owns so a half-addressed note is never saved.
  const resolveNoteAudience = (tokens: string[]): Promise<ResolveAudienceResponse> =>
    request('/admin/notes/audience/resolve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tokens }),
    })

  const createNote = (payload: CreateNotePayload): Promise<AuthoredNote> =>
    request('/admin/notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

  const updateNote = (
    noteId: string,
    payload: Partial<Pick<CreateNotePayload, 'body' | 'title' | 'severity' | 'starts_at' | 'expires_at'>>,
  ): Promise<AuthoredNote> =>
    request(`/admin/notes/${noteId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

  const publishNote = (noteId: string): Promise<PublishNoteResponse> =>
    request(`/admin/notes/${noteId}/publish`, { method: 'POST' })

  const retireNote = (noteId: string): Promise<AuthoredNote> =>
    request(`/admin/notes/${noteId}/retire`, { method: 'POST' })

  const parseBenefits = (
    organizationId: string,
    benefitSetId: string,
  ): Promise<{ benefits: Benefit[] }> =>
    request('/admin/benefits/parse', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        organization_id: organizationId,
        benefit_set_id: benefitSetId,
      }),
    })

  // One transaction server-side; the default set's text is mirrored onto the org.
  const updateBenefitSetSource = (
    organizationId: string,
    benefitSetId: string,
    companyBenefits: string | null,
  ): Promise<{ benefit_set_id: string; company_benefits: string | null }> =>
    request(`/admin/benefit-sets/${encodeURIComponent(benefitSetId)}/source`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        organization_id: organizationId,
        company_benefits: companyBenefits,
      }),
    })

  // Replace one member's non-default set assignments in one transaction.
  // An empty list clears them; the response lists what the member belongs to now.
  const setMemberBenefitSets = (
    organizationId: string,
    userId: string,
    benefitSetIds: string[],
  ): Promise<{ user_id: string; benefit_set_ids: string[] }> =>
    request(
      `/admin/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}/benefit-sets`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ benefit_set_ids: benefitSetIds }),
      },
    )

  const extractBenefitsFile = (
    organizationId: string,
    file: File,
  ): Promise<{ source_text: string }> => {
    const form = new FormData()
    form.append('organization_id', organizationId)
    form.append('file', file)
    // Do NOT set Content-Type — the browser sets the multipart boundary.
    return request('/admin/benefits/extract', {
      method: 'POST',
      body: form,
    })
  }

  const getBenefitUtilization = (organizationId: string): Promise<{ categories: CategoryUtilization[] }> =>
    request('/admin/benefits/utilization', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: organizationId }),
    })

  const updateOrganization = (
    organizationId: string,
    patch: OrganizationIdentityPatch,
  ): Promise<UpdateOrganizationResponse> =>
    request(`/admin/organizations/${organizationId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })

  const getCompanyMetrics = (
    organizationId: string,
    months: 3 | 6 | 12 | 'launch',
  ): Promise<CompanyMetrics> =>
    request('/admin/company-metrics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: organizationId, months }),
    })

  const getMemberActivity = (
    organizationId: string,
    weeks: 4 | 8 | 12,
  ): Promise<MemberActivityResponse> =>
    request('/admin/member-activity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: organizationId, weeks }),
    })

  const getMemberTimeline = (
    organizationId: string,
    weeks: 4 | 8 | 12,
    userId?: string,
  ): Promise<MemberTimelineResponse> =>
    request('/admin/member-activity/timeline', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        organization_id: organizationId,
        weeks,
        ...(userId ? { user_id: userId } : {}),
      }),
    })

  const getActivationReport = (
    organizationId: string,
  ): Promise<ActivationReportResponse> =>
    request('/admin/activation-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: organizationId }),
    })

  const sendAdhocEmail = (
    payload: SendAdhocEmailPayload,
  ): Promise<SendAdhocEmailResponse> =>
    request('/admin/adhoc-email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

  const formatAdhocEmail = (
    payload: FormatAdhocEmailPayload,
  ): Promise<FormatAdhocEmailResponse> =>
    request('/admin/adhoc-email/format', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

  const fetchAdhocEmailTemplates = (): Promise<ListAdhocEmailTemplatesResponse> =>
    request('/admin/adhoc-email/templates', { method: 'GET' })

  // The supplement catalog (super admin). Reads only: the catalog changes by a
  // pull request that edits services/ai/domain_knowledge/supplements_evidence/,
  // and a miss closes itself once the deployed catalog matches it.
  const getCatalogMisses = (limit: number): Promise<CatalogMissesResponse> => {
    const params = new URLSearchParams({ limit: String(limit) })
    return request(`/supplement-catalog/misses?${params.toString()}`)
  }

  const browseCatalogProducts = (query: CatalogProductsQuery): Promise<CatalogProductsPage> => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value !== null && value !== undefined && value !== '') params.set(key, String(value))
    }
    return request(`/supplement-catalog/products?${params.toString()}`)
  }

  const getCatalogRecord = (
    kind: CatalogRecordKind,
    recordId: string,
  ): Promise<CatalogRecordResponse> =>
    request(`/supplement-catalog/${kind}/${encodeURIComponent(recordId)}`)

  // Coach earnings and coach links (super admin). The report is one month of
  // what Mos owes coaches; the other calls settle it. A 503 means the store
  // could not be read, and the report then says nothing rather than an empty
  // month.
  const getCoachEarningsReport = (month: string): Promise<CoachEarningsReport> => {
    const params = new URLSearchParams({ month })
    return request(`/admin/coach-earnings?${params.toString()}`)
  }

  const recordCoachEarning = (orderId: string): Promise<CoachEarning> =>
    request('/admin/coach-earnings/record', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_id: orderId }),
    })

  const voidCoachEarning = (earningId: string, reason: string): Promise<CoachEarning> =>
    request(`/admin/coach-earnings/${encodeURIComponent(earningId)}/void`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    })

  const markCoachEarningsPaid = (payload: CoachPayoutRequest): Promise<CoachPayoutResult> =>
    request('/admin/coach-earnings/mark-paid', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

  const findCoachLink = (slug: string): Promise<CoachLinkAdmin> => {
    const params = new URLSearchParams({ slug })
    return request(`/admin/coach-links?${params.toString()}`)
  }

  const suspendCoachLink = (
    coachUserId: string,
    payload: CoachLinkSuspendRequest,
  ): Promise<CoachLinkAdmin> =>
    request(`/admin/coach-links/${encodeURIComponent(coachUserId)}/suspend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

  const unsuspendCoachLink = (coachUserId: string): Promise<CoachLinkAdmin> =>
    request(`/admin/coach-links/${encodeURIComponent(coachUserId)}/unsuspend`, {
      method: 'POST',
    })

  return {
    getAdminMe,
    sendInvitations,
    regenerateDietEmail,
    fetchDietSettings,
    updateMenuAdditionalInfo,
    createOrganization,
    getOrganizationPlan,
    createOrganizationPlan,
    updateOrganizationPlan,
    getSubscriptionLookupByEmail,
    createAdHocIndividualPlan,
    grantBonusDays,
    setCurrentUserPlan,
    getUserPlans,
    deactivateUserPlan,
    getOrganizationInvitations,
    resendOrganizationInvitation,
    cancelOrganizationInvitation,
    parseBenefits,
    updateBenefitSetSource,
    setMemberBenefitSets,
    listNotes,
    resolveNoteAudience,
    createNote,
    updateNote,
    publishNote,
    retireNote,
    extractBenefitsFile,
    getBenefitUtilization,
    getShareLink,
    getOpenJoinLink,
    updateOrganization,
    getCompanyMetrics,
    getMemberActivity,
    getMemberTimeline,
    getActivationReport,
    sendAdhocEmail,
    formatAdhocEmail,
    fetchAdhocEmailTemplates,
    getCatalogMisses,
    browseCatalogProducts,
    getCatalogRecord,
    getCoachEarningsReport,
    recordCoachEarning,
    voidCoachEarning,
    markCoachEarningsPaid,
    findCoachLink,
    suspendCoachLink,
    unsuspendCoachLink,
  }
}
